import * as XLSX from "xlsx";
import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  loadSchoolsList,
  loadResultsList,
  finalizeAssessment,
  retrieveSchoolYears,
} from "../../../actions/admin";
import { setAlert } from "../../../actions/alerts";
import Loading from "../../layouts/Loading";
import Alert from "../../layouts/Alert";
import { useHistory } from "react-router-dom";
import YearDropDown from "../../layouts/YearDropDown";
import _ from "lodash";

const ResultFinalize = ({
  user,
  isAuthenticated,
  schoolsList,
  loadSchoolsList,
  schoolListLoading,
  resultsList,
  loadResultsList,
  resultListLoading,
  finalizeAssessment,
  setAlert,
  schoolYears,
  retrieveSchoolYears,
}) => {
  const hist = useHistory();

  const [formData, setFormData] = useState({
    BenchMarkID: 1,
    Year: schoolYears?.endYear || new Date().getFullYear() + 1,
  });
  const [schoolID, setSchoolID] = useState(user?.SchoolID || 0);
  const { BenchMarkID, Year } = formData;

  // Initialize Year from schoolYears
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      Year: schoolYears?.endYear || new Date().getFullYear() + 1,
    }));
  }, [schoolYears]);

  // Ensure school years are available
  useEffect(() => {
    if (!schoolYears?.endYear) retrieveSchoolYears(new Date());
  }, [schoolYears, retrieveSchoolYears]);

  // Load schools if not available
  useEffect(() => {
    if (!schoolsList.length && schoolListLoading) loadSchoolsList();
    if (user) setSchoolID(user.SchoolID);
  }, [schoolsList, schoolListLoading, user, loadSchoolsList]);

  // Load results
  useEffect(() => {
    if (user) loadResultsList();
  }, [user, loadResultsList]);

  /** ------------------- Helper Functions ------------------- **/

  const computeDenseRank = (scores) => {
    // Sort scores descending (higher score = higher rank)
    const sorted = [...scores].sort((a, b) => a - b);
    const rankMap = {};
    let currentRank = 1;

    for (let i = 0; i < sorted.length; i++) {
      const score = sorted[i];
      if (!(score in rankMap)) {
        rankMap[score] = currentRank;
        currentRank += 1;
      }
    }

    return rankMap;
  };

  const computePercentile = (score, scores) => {
    const total = scores.length;
    const below = scores.filter((s) => s < score).length;
    const equal = scores.filter((s) => s === score).length;
    return ((below + 0.5 * equal) / total) * 100;
  };

  const categorizeScore = (score, ranges) => {
    if (score >= ranges.aboveBenchmark[0] && score <= ranges.aboveBenchmark[1])
      return 1;
    if (score >= ranges.atBenchmark[0] && score <= ranges.atBenchmark[1])
      return 2;
    if (score >= ranges.belowBenchmark[0] && score <= ranges.belowBenchmark[1])
      return 3;
    if (
      score >= ranges.wellBelowBenchmark[0] &&
      score <= ranges.wellBelowBenchmark[1]
    )
      return 4;
    return 0;
  };

  const encodeCompositeScore = (leftValue, rightValue) => {
    const normalizedLeft = parseInt(leftValue, 10) || 0;
    const normalizedRight = parseInt(rightValue, 10) || 0;
    const splitIndex = Math.max(String(normalizedLeft).length - 1, 0);

    return parseInt(`${normalizedLeft}${normalizedRight}${splitIndex}`, 10);
  };

  const rankGrades = (arr) => {
    // Step 1: Filter results
    const filtered = arr.filter(
      (item) =>
        item.TotalScore !== null &&
        item.SchoolID === parseInt(schoolID) &&
        item.BenchMarkID === parseInt(BenchMarkID) &&
        item.ClassroomYear === parseInt(Year),
    );

    // Step 2: Group by GradeID + CategoryID (Assessment type)
    const grouped = _.groupBy(
      filtered,
      (r) => `${r.ClassroomGrade}-${r.CategoryID}`,
    );
    const finalResults = [];

    // Step 3: Compute rank & percentile per group
    for (const key in grouped) {
      const group = grouped[key];

      if (group[0].CategoryID !== 4 && group[0].CategoryID !== 3) {
        // Normal assessments
        const scores = group.map((r) => r.TotalScore);
        const rankMap = computeDenseRank(scores);

        group.forEach((item) => {
          item.Rank = rankMap[item.TotalScore];
          item.Percentile = computePercentile(item.TotalScore, scores);

          const ranges = {
            aboveBenchmark: [item.aboveBenchmarkMin, item.aboveBenchmarkMax],
            atBenchmark: [item.atBenchmarkMin, item.atBenchmarkMax],
            belowBenchmark: [item.belowBenchmarkMin, item.belowBenchmarkMax],
            wellBelowBenchmark: [
              item.wellBelowBenchmarkMin,
              item.wellBelowBenchmarkMax,
            ],
          };
          item.riskStatus = categorizeScore(item.TotalScore, ranges);
          finalResults.push(item);
        });
      } else if (group[0].CategoryID === 3) {
        // Story Reading: CWPM (left) + Accuracy % (right)
        const cwpmScores = group.map((r) => parseInt(r.CWPM, 10) || 0);

        const accuracyScores = group.map((r) => {
          const cwpm = parseInt(r.CWPM, 10) || 0;
          const wr = parseInt(r.WordReadNumber, 10) || 0;
          return wr > 0 ? Math.round((cwpm / wr) * 100) : 0;
        });

        const cwpmRankMap = computeDenseRank(cwpmScores);
        const accuracyRankMap = computeDenseRank(accuracyScores);

        group.forEach((item, idx) => {
          const cwpmScore = parseInt(item.CWPM, 10) || 0;
          const accuracyScore = accuracyScores[idx];
          const cwpmRank = cwpmRankMap[cwpmScore] || 0;
          const accuracyRank = accuracyRankMap[accuracyScore] || 0;
          const cwpmPercentile = Math.round(
            computePercentile(cwpmScore, cwpmScores),
          );
          const accuracyPercentile = Math.round(
            computePercentile(accuracyScore, accuracyScores),
          );

          console.log("test compute OPer", cwpmScore, accuracyPercentile);

          item.Rank = encodeCompositeScore(cwpmRank, accuracyRank);
          item.Percentile = encodeCompositeScore(
            cwpmPercentile,
            accuracyPercentile,
          );

          const ranges = {
            aboveBenchmark: [item.aboveBenchmarkMin, item.aboveBenchmarkMax],
            atBenchmark: [item.atBenchmarkMin, item.atBenchmarkMax],
            belowBenchmark: [item.belowBenchmarkMin, item.belowBenchmarkMax],
            wellBelowBenchmark: [
              item.wellBelowBenchmarkMin,
              item.wellBelowBenchmarkMax,
            ],
          };
          item.riskStatus = categorizeScore(cwpmScore, ranges);
          finalResults.push(item);
        });
      } else {
        // CLS / WWR
        const clsScores = group.map((r) => (r.TotalCLS ? r.TotalCLS : 0));
        const wwrScores = group.map((r) => (r.TotalWWR ? r.TotalWWR : 0));
        const clsRankMap = computeDenseRank(clsScores);
        const wwrRankMap = computeDenseRank(wwrScores);

        group.forEach((item) => {
          const clsScore = parseInt(item.TotalCLS, 10) || 0;
          const wwrScore = parseInt(item.TotalWWR, 10) || 0;
          const clsRank = clsRankMap[clsScore] || 0;
          const wwrRank = wwrRankMap[wwrScore] || 0;
          const clsPercentile = Math.round(
            computePercentile(clsScore, clsScores),
          );
          const wwrPercentile = Math.round(
            computePercentile(wwrScore, wwrScores),
          );

          item.Rank = encodeCompositeScore(clsRank, wwrRank);
          item.Percentile = encodeCompositeScore(clsPercentile, wwrPercentile);

          const ranges = {
            aboveBenchmark: [item.aboveBenchmarkMin, item.aboveBenchmarkMax],
            atBenchmark: [item.atBenchmarkMin, item.atBenchmarkMax],
            belowBenchmark: [item.belowBenchmarkMin, item.belowBenchmarkMax],
            wellBelowBenchmark: [
              item.wellBelowBenchmarkMin,
              item.wellBelowBenchmarkMax,
            ],
          };
          item.riskStatus = categorizeScore(clsScore, ranges);
          finalResults.push(item);
        });
      }
    }

    return finalResults;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!schoolID || schoolID === 0) {
      setAlert("Please select a school", "danger");
      return;
    }

    const rankedResults = rankGrades(resultsList);

    // Test Function: Cross check the result in excel
    // if (rankedResults && rankedResults.length > 0) {
    //   const worksheet = XLSX.utils.json_to_sheet(rankedResults);
    //   const workbook = XLSX.utils.book_new();
    //   XLSX.utils.book_append_sheet(workbook, worksheet, "Results");
    //   XLSX.writeFile(workbook, "finalized_results.xlsx");
    // }

    finalizeAssessment(rankedResults).then(() => {
      setFormData({
        BenchMarkID: 1,
        Year: schoolYears?.endYear ?? new Date().getFullYear(),
      });
      setTimeout(() => hist.push("/admin/result"), 3000);
    });
  };

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.id]: e.target.value });

  return (
    <Fragment>
      {user && isAuthenticated && !schoolListLoading && !resultListLoading ? (
        <div className="container mx-auto my-5" style={{ paddingTop: "80px" }}>
          <div className="mb-3">
            <div className="d-sm-flex w-100 align-items-center justify-content-between">
              <div className="d-flex mb-2 mb-sm-0">
                <div
                  className="admin-back mx-2 rounded-circle d-flex align-items-center justify-content-center txt-primary"
                  onClick={() => hist.goBack()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    className="bi bi-arrow-left-short"
                    viewBox="0 0 16 16"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z"
                    />
                  </svg>
                </div>
                <h4 className="m-0">Assessment</h4>
              </div>
            </div>
          </div>
          <div className="d-flex position-relative">
            <div className="container card shadow border-0 w-100">
              <div className="card-body mt-1">
                <h4>Benchmark Calculation</h4>
                <Alert />
                <form onSubmit={onSubmit}>
                  {user.SchoolID === null && (
                    <div className="mb-3">
                      <label>
                        <b>School</b>
                      </label>
                      <select
                        className="form-select form-control border-primary"
                        id="SchoolID"
                        value={schoolID}
                        onChange={(e) => setSchoolID(e.target.value)}
                      >
                        <option value={0}>Select School</option>
                        {schoolsList
                          .sort((a, b) => a.Name.localeCompare(b.Name))
                          .map((school) => (
                            <option
                              key={school.SchoolID}
                              value={school.SchoolID}
                            >
                              {school.Name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div className="mt-3">
                    <label>
                      <b>Benchmark</b>
                    </label>
                    <select
                      className="form-select form-control border-primary"
                      value={BenchMarkID}
                      id="BenchMarkID"
                      onChange={onChange}
                    >
                      <option value={1}>Benchmark 1 – Fall</option>
                      <option value={2}>Benchmark 2 – Winter</option>
                      <option value={3}>Benchmark 3 – Spring</option>
                      <option value={4}>Progress Monitoring</option>
                    </select>
                  </div>

                  <div className="mt-3">
                    <label>
                      <b>Year</b>
                    </label>
                    <select
                      className="form-select form-control border-primary"
                      value={Year}
                      id="Year"
                      onChange={onChange}
                    >
                      <option value={-1}>Select Year</option>
                      <YearDropDown schoolYears={schoolYears} />
                    </select>
                  </div>

                  <div className="d-flex align-items-center justify-content-end my-3">
                    <button
                      type="submit"
                      className="button-primary btn-block btn px-5"
                    >
                      Finalize
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center h-100 justify-content-center">
          <Loading />
        </div>
      )}
    </Fragment>
  );
};

ResultFinalize.propTypes = {
  user: PropTypes.object,
  isAuthenticated: PropTypes.bool.isRequired,
  schoolsList: PropTypes.array.isRequired,
  loadSchoolsList: PropTypes.func.isRequired,
  finalizeAssessment: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  schoolYears: PropTypes.object,
  retrieveSchoolYears: PropTypes.func.isRequired,
  resultsList: PropTypes.array.isRequired,
  resultListLoading: PropTypes.bool,
  schoolListLoading: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  isAuthenticated: state.auth.isAuthenticated,
  schoolsList: state.admin.schoolsList,
  schoolListLoading: state.admin.schoolListLoading,
  resultsList: state.admin.resultsList,
  resultListLoading: state.admin.resultListLoading,
  schoolYears: state.admin.schoolYears,
});

export default connect(mapStateToProps, {
  loadSchoolsList,
  loadResultsList,
  finalizeAssessment,
  setAlert,
  retrieveSchoolYears,
})(ResultFinalize);
